import React, { useContext } from 'react';
import {
  AspectRatio, Box, Grid, Heading, HStack, Link, Text,
} from '@chakra-ui/react';
import Image from 'next/image';
import EventFacts from './EventFacts';
import MarkdownView from '../markdownView/MarkdownView';
import { NavigationContext } from '../../lib/contexts/NavigationContext';
import { getLongDate, getTimeFromDate } from '../../utils/dateUtils';

interface Props { event: any; children?: React.ReactNode }

// A calm, faintly indigo ground. The page paints it edge to edge itself, which
// also hides the site's fixed ribbon image here: behind a ticket card and a
// hero it was one more surface competing for attention.
const GROUND = '#f7f7fb';

// Two columns only from lg: between md and lg a five-column ticket card is too
// narrow for a band name, its price and a stepper side by side.
const COLUMNS = ['1fr', '1fr', '1fr', 'repeat(12, minmax(0, 1fr))'];

/**
 * One live event, given the whole page.
 *
 * With a single event, a grid holding one card reads as a page that failed to
 * load. Showing the event itself also removes a hop: the customer lands on
 * Events and can choose tickets without a second page load, which matters on
 * the mobile connections these pages are opened over.
 *
 * Layout: a full-width indigo hero with the date, title and venue, then the
 * description beside a ticket card that overlaps the hero and stays in view
 * as the description scrolls. On narrow screens the tickets come straight
 * after the hero, ahead of the description, rather than below all of it.
 */
const SingleEventFeature = ({ event, children }: Props) => {
  const { paths } = useContext(NavigationContext);
  const start = event.startDateTime ? new Date(event.startDateTime) : null;

  return (
    <Box
      // Break out of the page's max-width container and cancel DefaultLayout's
      // padding (p={[4, 12]}), so the hero meets the header and the ground
      // meets the footer. Keep these in step with that padding.
      w="100vw"
      ml="calc(50% - 50vw)"
      mt={[-4, -12]}
      mb={[-4, -12]}
      bg={GROUND}
    >
      <Box bg="blue.900" color="white">
        <Box maxW="1200px" mx="auto" px={[5, 6, 8]} pt={[6, 9]} pb={[8, 9, 9, 20]}>
          <Grid templateColumns={COLUMNS} columnGap={12}>
            <Box gridColumn={['auto', 'auto', 'auto', 'span 7']}>
              <HStack as="nav" aria-label="Breadcrumb" spacing={2} fontSize="13px" color="whiteAlpha.700" mb={[7, 10]}>
                <Link href={paths?.events} _hover={{ color: 'white' }}>Events</Link>
                <Text aria-hidden>/</Text>
                <Text color="whiteAlpha.900" noOfLines={1}>{event.name}</Text>
              </HStack>

              {start && (
                <Text
                  fontSize={['13px', '14px']}
                  fontWeight="bold"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  color="yellow.900"
                  mb={4}
                >
                  {getLongDate(start)}
                  {' · '}
                  {getTimeFromDate(start)}
                </Text>
              )}

              {/* Larken ships in one weight. Asking for bold made the browser
                  synthesise it, which smudged the brand face. */}
              <Heading
                as="h1"
                fontFamily="heading"
                fontWeight="400"
                fontSize={['46px', '60px', '60px', '60px', '76px']}
                lineHeight="1.04"
                letterSpacing="-0.01em"
                color="white"
                mb={5}
              >
                {event.name}
              </Heading>

              <EventFacts
                name={event.name}
                startDateTime={event.startDateTime}
                endDateTime={event.endDateTime}
                address={event.address}
                showDate={false}
                withActions
                actionsAs="link"
                onDark
                color="whiteAlpha.900"
                fontSize={['16px', '17px']}
              />
            </Box>
          </Grid>
        </Box>
      </Box>

      <Box maxW="1200px" mx="auto" px={[4, 6, 8]} pb={[12, 24]}>
        <Grid templateColumns={COLUMNS} columnGap={12} alignItems="start">
          <Box as="article" gridColumn={['auto', 'auto', 'auto', 'span 7']} pt={[10, 10, 10, 14]} maxW="640px">
            {event.image && (
              <AspectRatio
                ratio={16 / 9}
                borderRadius="14px"
                overflow="hidden"
                mb={10}
                boxShadow="0 18px 40px -22px rgba(45, 44, 122, 0.45)"
              >
                <Image
                  src={event.image.split('?')[0]}
                  alt={event.name}
                  fill
                  unoptimized
                  style={{ objectFit: 'cover' }}
                />
              </AspectRatio>
            )}
            {event.description && (
              <>
                <Heading as="h2" fontFamily="body" fontSize={['22px', '24px']} lineHeight="1.25" m={0} mb={4}>
                  About this event
                </Heading>
                {/* Authored as Markdown in the admin, the same as a course
                    group's description. Rendered as plain text it showed the
                    raw asterisks and ran every paragraph together, because JSX
                    collapses the newlines an author typed. */}
                <Box
                  color="gray.600"
                  fontSize={['16px', '17px']}
                  lineHeight="1.7"
                  sx={{ '& p': { marginBottom: '16px' }, '& li': { marginBottom: '4px' } }}
                >
                  <MarkdownView markdown={event.description} nested />
                </Box>
              </>
            )}
          </Box>

          <Box
            as="aside"
            gridColumn={['auto', 'auto', 'auto', 'span 5']}
            order={[-1, -1, -1, 0]}
            mt={[5, 6, 6, '-268px']}
            position={['static', 'static', 'static', 'sticky']}
            top="128px"
          >
            {children}
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default SingleEventFeature;
